import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { BaseComponent } from '../../../core/components/base/base.component';
import { FeedbackService } from '../../../core/services/feedback.service';
import { patientReportedLabel, withPatientReported } from '../../../core/utils/patient-reported';

interface CommonCondition {
  id: string;
  name: string;
  hint: string;
  checked: boolean;
}

@Component({
  selector: 'app-medical-history-list-creator',
  templateUrl: './medical-history-list-creator.component.html',
  styleUrls: ['./medical-history-list-creator.component.scss'],
})
export class MedicalHistoryListCreatorComponent extends BaseComponent implements OnChanges, OnInit {
  /** The "Patient reported" mark of a row the patient gave (their invite, or the patient app). */
  readonly reported = patientReportedLabel;

  @Input() heading: string = 'Medical History / Operations';
  @Input() history: any[] = [];
  @Input() noHistory: boolean = false;
  @Input() hideActions: boolean = false;
  @Input() hideCheckbox: boolean = false;
  @Input() showCommonConditionsByDefault: boolean = false;
  @Output() historyChanged = new EventEmitter<any[]>();
  @Output() noHistoryChanged = new EventEmitter<boolean>();

  editingHistory: any = {};
  showCommonConditions: boolean = false;
  private currentDialogRef?: any;
  private dialogOpenedFromOther: boolean = false;

  @ViewChild('addHistoryDialog') addHistoryDialog?: TemplateRef<any>;

  commonConditions: CommonCondition[] = [
    { id: 'hypertension', name: 'Hypertension', hint: 'High Blood Pressure', checked: false },
    { id: 'diabetes', name: 'Diabetes', hint: 'Type 1 or Type 2', checked: false },
    { id: 'asthma', name: 'Asthma or Chronic Lung Disease', hint: 'e.g. COPD', checked: false },
    { id: 'heart-disease', name: 'Heart Disease', hint: 'e.g. Angina, Heart Attack', checked: false },
    { id: 'stroke', name: 'Stroke or TIA', hint: 'Transient Ischemic Attack', checked: false },
    { id: 'epilepsy', name: 'Epilepsy or Seizure Disorder', hint: '', checked: false },
    { id: 'kidney-disease', name: 'Kidney Disease', hint: '', checked: false },
    { id: 'liver-disease', name: 'Liver Disease or Hepatitis', hint: '', checked: false },
    { id: 'hiv', name: 'HIV or Other Immunocompromised Conditions', hint: '', checked: false },
    { id: 'tuberculosis', name: 'Tuberculosis', hint: 'Past or Current', checked: false },
    { id: 'cancer', name: 'Cancer', hint: 'Any Type', checked: false },
    { id: 'mental-health', name: 'Depression, Anxiety, or Other Mental Health Conditions', hint: '', checked: false },
    { id: 'thyroid', name: 'Thyroid Problems', hint: 'Hypo/Hyperthyroidism', checked: false },
    { id: 'blood-disorders', name: 'Blood Disorders', hint: 'e.g. Anaemia, Clotting Disorders', checked: false },
    { id: 'chronic-pain', name: 'Chronic Pain Conditions', hint: 'e.g. Fibromyalgia, Arthritis', checked: false },
    { id: 'other', name: 'Other', hint: 'Specify', checked: false }
  ];

  constructor(fs: FeedbackService, md: MatDialog) {
    super(fs, md);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['history'] && this.history.length > 0) {
      this.noHistory = false;
      this.syncChecklistWithHistory();
    }
    
    if (changes['showCommonConditionsByDefault']) {
      this.showCommonConditions = this.showCommonConditionsByDefault;
    }
  }

  ngOnInit(): void {
    // Initialize the show state based on the input
    this.showCommonConditions = this.showCommonConditionsByDefault;
    
    // Sync with existing history
    if (this.history.length > 0) {
      this.syncChecklistWithHistory();
    }
  }

  syncChecklistWithHistory(): void {
    // Reset all checkboxes
    this.commonConditions.forEach(condition => {
      condition.checked = false;
    });

    // Check boxes based on existing history
    this.history.forEach(item => {
      const condition = this.commonConditions.find(c => 
        c.name.toLowerCase() === item.surgery.toLowerCase()
      );
      if (condition) {
        condition.checked = true;
      }
    });
  }

  onConditionToggle(condition: CommonCondition): void {
    if (condition.checked) {
      // Handle "Other" condition specially
      if (condition.id === 'other') {
        this.dialogOpenedFromOther = true;
        this.openAddHistoryDialog();
        return;
      }
      
      // Add to history if not already present
      const exists = this.history.some(item => 
        item.surgery.toLowerCase() === condition.name.toLowerCase()
      );
      if (!exists) {
        this.history.push({ surgery: condition.name, year: '' });
        this.emitHistory();
      }
    } else {
      // Handle "Other" condition specially
      if (condition.id === 'other') {
        // Don't remove from history for "Other" - just uncheck
        return;
      }
      
      // Remove from history
      const index = this.history.findIndex(item => 
        item.surgery.toLowerCase() === condition.name.toLowerCase()
      );
      if (index !== -1) {
        this.history.splice(index, 1);
        this.emitHistory();
      }
    }
  }

  trackByCondition(index: number, condition: CommonCondition): string {
    return condition.id;
  }

  trackByHistoryItem(index: number, item: any): string {
    return item.id || item.surgery;
  }

  toggleCommonConditions(): void {
    this.showCommonConditions = !this.showCommonConditions;
  }

  openAddHistoryDialog(): void {
    this.noHistory = false;
    this.editingHistory = { id: this.history.length + 1 };
    this.currentDialogRef = this.dialog.open(this.addHistoryDialog!);
    
    // Subscribe to afterClosed to handle cancellation
    this.currentDialogRef.afterClosed().subscribe((result: any) => {
      // If dialog was closed without saving and was opened from "Other", untick the "Other" checkbox
      if (!result && this.dialogOpenedFromOther) {
        const otherCondition = this.commonConditions.find(c => c.id === 'other');
        if (otherCondition) {
          otherCondition.checked = false;
        }
      }
      // Reset the flag
      this.dialogOpenedFromOther = false;
    });
  }

  getTooltipText(history: any): string {
    const yearText = history.year && history.year.trim() ? history.year : 'Not Provided';
    return withPatientReported(`${history.surgery} - Year: ${yearText}`, history);
  }

  editHistory(history: any): void {
    this.editingHistory = { ...history };
    this.dialogOpenedFromOther = false; // Reset flag since this is editing, not adding from "Other"
    this.currentDialogRef = this.dialog.open(this.addHistoryDialog!);
  }

  saveHistory(): void {
    if (!this.editingHistory.surgery) return;
    const index = this.history.findIndex(c => c.id === this.editingHistory.id);
    if (index !== -1) {
      this.history[index] = this.editingHistory;
    } else {
      this.history.push(this.editingHistory);
    }
    this.syncChecklistWithHistory();
    this.emitHistory();
    this.currentDialogRef.close(this.editingHistory);
  }

  cancelEdit(): void {
    this.currentDialogRef.close();
  }

  removeHistory(item: any): void {
    const index = this.history.indexOf(item);
    if (index !== -1) {
      this.history.splice(index, 1);
      this.syncChecklistWithHistory();
      this.emitHistory();
    }
  }

  async addChip(item: any): Promise<void> {
    this.history.push({ surgery: item.surgery, year: item.year });
    this.syncChecklistWithHistory();
    this.historyChanged.emit(this.history);
    return;
  }

  toggleNoHistory(): void {
    this.noHistoryChanged.emit(this.noHistory);
    if (this.noHistory) {
      this.historyChanged.emit([]);
    } else {
      this.emitHistory();
    }
  }

  private emitHistory(): void {
    const emittedList = this.history.map(item => {
      if (typeof item.id === 'number') {
        const { id, ...rest } = item;
        return rest;
      }
      return item;
    });
    this.historyChanged.emit(emittedList);
  }
}